import { ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';
import { DevicesStore, InstallationFeaturesStore } from '@store';
import {
  ActivateDeactivateCommands,
  DeviceIds,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  RoomsFeaturesProperties,
  RoomsStatusProperties,
  SrcCondensateAvoidanceCommands,
  SrcCondensateAvoidanceStatusProperties,
  SrcPumpControlStatusProperties,
} from '@vi-iot/client/index';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Bool } from 'src/app/shared/common/enums';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-src-settings-algorithms',
  templateUrl: './src-settings-algorithms.component.html',
  styleUrls: ['./src-settings-algorithms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcSettingsAlgorithmsComponent implements OnInit {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  isSupplyChannelToggleAvailable: boolean;

  pumpControlIds = ['0', '1', '2', '3'];

  listPumpControlFeatures: Feature<
    SrcPumpControlStatusProperties,
    ActivateDeactivateCommands
  >[] = [];

  listFtdcFeatures$: Observable<
    Feature<SrcPumpControlStatusProperties, SrcCondensateAvoidanceCommands>[]
  >;

  status$: Observable<Feature<RoomsStatusProperties, ActivateDeactivateCommands>>;
  condensateFeatures$: Observable<
    Feature<SrcCondensateAvoidanceStatusProperties, SrcCondensateAvoidanceCommands>
  >;
  featurePropertyKey = FeaturePropertyKey;
  bool = Bool;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    protected installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore,
    protected translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomStatus,
      DeviceIds.RoomControl
    );

    const pumpControlFeatures = this.pumpControlIds.map((pumpId) =>
      this.getSrcChannelPumpControl(pumpId).pipe(
        take(1),
        tap((feature) => {
          if (feature) {
            this.listPumpControlFeatures.push(feature);
          }
        })
      )
    );

    this.condensateFeatures$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      SrcCondensateAvoidanceStatusProperties,
      SrcCondensateAvoidanceCommands
    >(FeatureNames.condensateAvoidance, DeviceIds.RoomControl);

    this.listFtdcFeatures$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<RoomsFeaturesProperties, Record<string, never>>(
        FeatureNames.roomsFeatures,
        DeviceIds.RoomControl
      )
      .pipe(
        filter((roomsFeatures) => !!roomsFeatures),
        switchMap((roomsFeatures) => {
          const supported: string[] = roomsFeatures?.properties?.supported?.value ?? [];
          const ftdcChannelIds = supported
            .map((s) => {
              const match = s.match(/^supplyChannel(\d+)FTDC$/);

              return match ? match[1] : null;
            })
            .filter((id): id is string => id !== null);

          if (ftdcChannelIds.length === 0) {
            return of([]);
          }

          return combineLatest(
            ftdcChannelIds.map((channelId) =>
              this.installationFeaturesStore
                .selectInstallationDeviceFeature<
                  SrcPumpControlStatusProperties,
                  SrcCondensateAvoidanceCommands
                >(FeatureNames.srcChannelFTDC(channelId), DeviceIds.RoomControl)
                .pipe(filter((f) => !!f))
            )
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      );

    forkJoin(pumpControlFeatures)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.isSupplyChannelToggleAvailable = this.listPumpControlFeatures.length > 0;
      });
  }

  getSrcChannelPumpControl(
    pumpId: string
  ): Observable<Feature<SrcPumpControlStatusProperties, ActivateDeactivateCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      SrcPumpControlStatusProperties,
      ActivateDeactivateCommands
    >(FeatureNames.srcChannelPumpControl(pumpId), DeviceIds.RoomControl);
  }
}
